import { Box, FormControl, FormControlLabel, FormHelperText } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnboardingLens } from '../onboardingLens';
import { onboardingCheckboxLabelSlotProps } from '../onboardingCheckboxLabelSlotProps';
import { onboardingCheckboxRowSx } from '../onboardingCheckboxRowSx';
import { OnboardingCheckbox } from '../OnboardingCheckbox';
import { OnboardingFormSelect } from '../OnboardingFormSelect';
import { OnboardingFormTextField } from '../OnboardingFormTextField';
import { OnboardingFieldLabel, OnboardingStepHeader } from '../OnboardingStepHeader';
import { OnboardingStepScrollLayout } from '../OnboardingStepScrollLayout';
import {
  type CompanyTerminologyFieldError,
  getCompanyTerminologyInputFeedback,
  sanitizeCompanyTerminologyInput,
} from '../companyTerminologyValidation';
import {
  ONBOARDING_EMPLOYEE_COUNT_OPTIONS,
  ONBOARDING_INDUSTRY_OPTIONS,
  ONBOARDING_REGION_COLUMNS,
} from '../onboardingConstants';

interface OnboardingStepCompanyProps {
  companyName: string;
  industry: string;
  employeeCount: string;
  regions: string[];
  employeeTerminology: string;
  speakUpSolution: string;
  onIndustryChange: (value: string) => void;
  onCompanyNameChange: (value: string) => void;
  onEmployeeCountChange: (value: string) => void;
  onRegionsChange: (value: string[]) => void;
  onEmployeeTerminologyChange: (value: string) => void;
  onSpeakUpSolutionChange: (value: string) => void;
  showErrors: boolean;
  fieldErrors: {
    companyName?: string;
    industry?: string;
    employeeCount?: string;
    regions?: string;
    employeeTerminology?: string;
    speakUpSolution?: string;
  };
}

/** Figma node 17837:37161 — About your company. */
export function OnboardingStepCompany({
  companyName,
  industry,
  employeeCount,
  regions,
  employeeTerminology,
  speakUpSolution,
  onIndustryChange,
  onCompanyNameChange,
  onEmployeeCountChange,
  onRegionsChange,
  onEmployeeTerminologyChange,
  onSpeakUpSolutionChange,
  showErrors,
  fieldErrors,
}: OnboardingStepCompanyProps) {
  const { t } = useTranslation();
  const { layout, colors } = useOnboardingLens();

  const [employeeTerminologyFeedback, setEmployeeTerminologyFeedback] = useState<
    CompanyTerminologyFieldError | undefined
  >();
  const [speakUpSolutionFeedback, setSpeakUpSolutionFeedback] = useState<
    CompanyTerminologyFieldError | undefined
  >();

  const terminologyWarningMessage = (feedback: CompanyTerminologyFieldError | undefined) => {
    if (!feedback) return undefined;
    const key = feedback === 'max_length' ? 'terminology_max_length' : feedback;
    return t(`onboarding.wizard.step3.errors.${key}`);
  };

  const handleEmployeeTerminologyChange = (raw: string) => {
    const sanitized = sanitizeCompanyTerminologyInput(raw);
    setEmployeeTerminologyFeedback(getCompanyTerminologyInputFeedback(raw));
    onEmployeeTerminologyChange(sanitized);
  };

  const handleSpeakUpSolutionChange = (raw: string) => {
    const sanitized = sanitizeCompanyTerminologyInput(raw);
    setSpeakUpSolutionFeedback(getCompanyTerminologyInputFeedback(raw));
    onSpeakUpSolutionChange(sanitized);
  };

  const companyNameError =
    showErrors && fieldErrors.companyName
      ? t(`onboarding.wizard.step3.errors.${fieldErrors.companyName}`)
      : undefined;

  const industryError =
    showErrors && fieldErrors.industry
      ? t(`onboarding.wizard.step3.errors.${fieldErrors.industry}`)
      : undefined;

  const employeeCountError =
    showErrors && fieldErrors.employeeCount
      ? t(`onboarding.wizard.step3.errors.${fieldErrors.employeeCount}`)
      : undefined;

  const regionsError =
    showErrors && fieldErrors.regions
      ? t(`onboarding.wizard.step3.errors.${fieldErrors.regions}`)
      : undefined;

  const employeeTerminologyError =
    showErrors && fieldErrors.employeeTerminology
      ? t(`onboarding.wizard.step3.errors.${fieldErrors.employeeTerminology}`)
      : undefined;

  const speakUpSolutionError =
    showErrors && fieldErrors.speakUpSolution
      ? t(`onboarding.wizard.step3.errors.${fieldErrors.speakUpSolution}`)
      : undefined;

  const handleRegionToggle = (region: string, checked: boolean) => {
    if (checked) {
      onRegionsChange([...regions, region]);
      return;
    }
    onRegionsChange(regions.filter((r) => r !== region));
  };

  const checkboxRowSx = {
    ...onboardingCheckboxRowSx({
      rowPaddingY: layout.regions.rowPaddingY,
    }),
    minHeight: layout.formSection.paddingY,
  };

  return (
    <OnboardingStepScrollLayout
      px={layout.formSection.paddingX}
      py={{ xs: layout.formSection.paddingY, sm: layout.formSection.paddingY }}
      headerGap={layout.formSection.sectionGap}
      backgroundColor={colors.surfaceDefault}
      header={
        <OnboardingStepHeader
          title={t('onboarding.wizard.step3.title')}
          subtitle={t('onboarding.wizard.step3.subtitle')}
          titleSubtitleGap={layout.formSection.headerSubtitleGap}
          subtitleBottomSpacing={layout.formSection.headerSubtitleBottomSpacing}
        />
      }
      bodySx={{
        display: 'flex',
        flexDirection: 'column',
        gap: layout.formSection.sectionGap,
        /** Room for focus ring without clipping at scroll edges. */
        px: layout.focusRingGutter,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ minWidth: 0, width: '100%' }}>
        <OnboardingFormTextField
          id="onboarding-company-name"
          label={t('onboarding.wizard.step3.company_name_label')}
          placeholder={t('onboarding.wizard.step3.company_name_placeholder')}
          value={companyName}
          onChange={(value) => onCompanyNameChange(value)}
          error={companyNameError}
          required
          maxLength={500}
        />
        {!companyNameError ? (
          <FormHelperText sx={{ mx: 0, mt: -1 }}>
            {t('onboarding.wizard.step3.company_name_helper')}
          </FormHelperText>
        ) : null}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: layout.dropdownRow.gap,
          width: '100%',
        }}
      >
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <OnboardingFormSelect
            id="onboarding-industry"
            label={t('onboarding.wizard.step3.industry_label')}
            placeholder={t('onboarding.wizard.step3.industry_placeholder')}
            value={industry}
            options={ONBOARDING_INDUSTRY_OPTIONS}
            onChange={(e) => onIndustryChange(e.target.value)}
            error={industryError}
            required
          />
        </Box>
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <OnboardingFormSelect
            id="onboarding-employee-count"
            label={t('onboarding.wizard.step3.employee_count_label')}
            placeholder={t('onboarding.wizard.step3.employee_count_placeholder')}
            value={employeeCount}
            options={ONBOARDING_EMPLOYEE_COUNT_OPTIONS}
            onChange={(e) => onEmployeeCountChange(e.target.value)}
            error={employeeCountError}
            required
          />
        </Box>
      </Box>

      <FormControl
        error={Boolean(regionsError)}
        component="fieldset"
        variant="standard"
        sx={{
          width: '100%',
          m: 0,
          p: 0,
          border: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: layout.formField.labelToInputGap,
        }}
      >
        <OnboardingFieldLabel label={t('onboarding.wizard.step3.regions_label')} required />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'flex-start',
            gap: layout.regions.columnGap,
            width: '100%',
          }}
        >
          {ONBOARDING_REGION_COLUMNS.map((column, columnIndex) => (
            <Box
              key={columnIndex === 0 ? 'regions-left' : 'regions-right'}
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {column.map((region) => (
                <FormControlLabel
                  key={region}
                  sx={checkboxRowSx}
                  control={
                    <OnboardingCheckbox
                      checked={regions.includes(region)}
                      onChange={(e) => handleRegionToggle(region, e.target.checked)}
                    />
                  }
                  label={region}
                  slotProps={onboardingCheckboxLabelSlotProps()}
                />
              ))}
            </Box>
          ))}
        </Box>
        {regionsError && (
          <FormHelperText error sx={{ mx: 0 }}>
            {regionsError}
          </FormHelperText>
        )}
      </FormControl>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: layout.dropdownRow.gap,
          width: '100%',
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <OnboardingFormTextField
            id="onboarding-employee-terminology"
            label={t('onboarding.wizard.step3.employee_terminology_label')}
            placeholder={t('onboarding.wizard.step3.employee_terminology_placeholder')}
            value={employeeTerminology}
            onChange={handleEmployeeTerminologyChange}
            error={employeeTerminologyError}
            warning={
              !employeeTerminologyError
                ? terminologyWarningMessage(employeeTerminologyFeedback)
                : undefined
            }
          />
        </Box>
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <OnboardingFormTextField
            id="onboarding-speak-up-solution"
            label={t('onboarding.wizard.step3.speak_up_solution_label')}
            placeholder={t('onboarding.wizard.step3.speak_up_solution_placeholder')}
            value={speakUpSolution}
            onChange={handleSpeakUpSolutionChange}
            error={speakUpSolutionError}
            warning={
              !speakUpSolutionError
                ? terminologyWarningMessage(speakUpSolutionFeedback)
                : undefined
            }
          />
        </Box>
      </Box>
    </OnboardingStepScrollLayout>
  );
}
